import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, Clock, AlertCircle, Mail } from 'lucide-react';
import Image from '../../components/common/Image';

type PageState = 'loading' | 'success' | 'error' | 'expired';

const FirstLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const token = searchParams.get('token');
  const errorType = searchParams.get('error');

  useEffect(() => {
    if (errorType === 'expired') setPageState('expired');
    else if (errorType === 'invalid') setPageState('error');
    else if (token) setPageState('success');
    else setPageState('error');
  }, [token, errorType]);

  const handleResendInvitation = async () => {
    setIsResending(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setResendSuccess(true);
    } catch (err) {
      console.error('Failed to resend invitation:', err);
    } finally {
      setIsResending(false);
    }
  };

  const handleGoToLogin = () => navigate('/login');

  const renderLoading = () => (
    <>
      <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
        <Loader2 className="w-9 h-9 text-primary animate-spin" />
      </div>
      <h1 className="text-xl font-semibold text-dark mb-2 tracking-tight">認証中...</h1>
      <p className="text-sm text-gray-500 leading-relaxed">しばらくお待ちください。</p>
    </>
  );

  const renderSuccess = () => (
    <>
      <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
        <CheckCircle2 className="w-10 h-10 text-success" />
      </div>
      <h1 className="text-xl font-semibold text-dark mb-2 tracking-tight">認証完了</h1>
      <p className="text-sm text-gray-500 leading-relaxed mb-6">
        アカウントの認証が完了しました。
      </p>
      <button
        type="button"
        onClick={handleGoToLogin}
        className="w-full bg-primary hover:bg-primary-hover text-white font-medium rounded-lg py-2.5 text-sm transition-colors"
      >
        ログインする
      </button>
    </>
  );

  const ResendArea = () =>
    resendSuccess ? (
      <p className="text-sm text-success mb-3">
        ✓ 招待メールを再送信しました。
        <br />
        メールをご確認ください。
      </p>
    ) : (
      <button
        type="button"
        onClick={handleResendInvitation}
        disabled={isResending}
        className="w-full bg-warm-surface hover:bg-warm-hover border border-warm-border text-dark font-medium rounded-lg py-2.5 text-sm transition-colors mb-3 disabled:opacity-60"
      >
        {isResending ? '送信中...' : '招待メールを再送信する'}
      </button>
    );

  const renderExpired = () => (
    <>
      <div className="w-20 h-20 rounded-full bg-warning/10 flex items-center justify-center mx-auto mb-6">
        <Clock className="w-10 h-10 text-warning" />
      </div>
      <h1 className="text-xl font-semibold text-dark mb-2 tracking-tight">リンクの有効期限切れ</h1>
      <p className="text-sm text-gray-500 leading-relaxed mb-6">
        この招待リンクは有効期限が切れています。
      </p>
      <ResendArea />
      <button
        type="button"
        onClick={handleGoToLogin}
        className="w-full bg-primary hover:bg-primary-hover text-white font-medium rounded-lg py-2.5 text-sm transition-colors"
      >
        ログインページへ
      </button>
      <div className="bg-warm-surface rounded-lg p-4 mt-6 text-left">
        <p className="text-sm font-semibold text-dark mb-1">お困りですか？</p>
        <a
          href="mailto:support@tasky.jp?subject=ログインに関するお問い合わせ"
          className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary-hover"
        >
          <Mail className="w-4 h-4" />
          support@tasky.jp
        </a>
      </div>
    </>
  );

  const renderError = () => (
    <>
      <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
        <AlertCircle className="w-10 h-10 text-destructive" />
      </div>
      <h1 className="text-xl font-semibold text-dark mb-2 tracking-tight">エラーが発生しました</h1>
      <p className="text-sm text-gray-500 leading-relaxed mb-6">
        認証処理中にエラーが発生しました。
        <br />
        お手数ですが、再度お試しいただくか、サポートにお問い合わせください。
      </p>
      <ResendArea />
      <button
        type="button"
        onClick={handleGoToLogin}
        className="w-full bg-primary hover:bg-primary-hover text-white font-medium rounded-lg py-2.5 text-sm transition-colors"
      >
        ログインページへ
      </button>
      <div className="bg-warm-surface rounded-lg p-4 mt-6 text-left">
        <p className="text-sm font-semibold text-dark mb-1">サポートが必要ですか？</p>
        <a
          href="mailto:support@tasky.jp?subject=ログインに関するお問い合わせ"
          className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary-hover"
        >
          <Mail className="w-4 h-4" />
          サポートに連絡する
        </a>
      </div>
    </>
  );

  return (
    <div className="bg-warm-bg min-h-screen flex items-center justify-center px-4 py-16 font-sans">
      <div className="w-full max-w-md bg-white rounded-xl p-8 sm:p-10 text-center shadow-sm">
        <div className="flex justify-center mb-8">
          <Image category="common" fileName="logo.png" alt="Tasky" className="h-10 w-auto" />
        </div>
        {pageState === 'loading' && renderLoading()}
        {pageState === 'success' && renderSuccess()}
        {pageState === 'expired' && renderExpired()}
        {pageState === 'error' && renderError()}
      </div>
    </div>
  );
};

export default FirstLoginPage;
